import { withoutViewport } from '../models/search-request';

export type SearchDomain = 'CAR' | 'REAL_ESTATE';
export type SearchMode = 'list' | 'map';
export interface MapViewport { latitude: number; longitude: number; zoom: number; }
export interface SearchHistoryEntry {
  id?: number;
  domain: SearchDomain;
  mode: SearchMode;
  criteria: Record<string, unknown>;
  viewport?: MapViewport;
  executedAtUtc?: string;
}
const boundsKeys = ['northEastLat', 'northEastLng', 'southWestLat', 'southWestLng'] as const;
const finite = (n: unknown): n is number => typeof n === 'number' && Number.isFinite(n);
type Point = { latitude: number; longitude: number };
function validPolygon(value: unknown): value is Point[] {
  if (!Array.isArray(value) || value.length < 3 || value.length > 101 ||
    !value.every(p => p && finite(p.latitude) && finite(p.longitude) && Math.abs(p.latitude) < 90 && Math.abs(p.longitude) <= 180)) return false;
  const same = (a: Point, b: Point) => a.latitude === b.latitude && a.longitude === b.longitude;
  const points: Point[] = same(value[0], value[value.length - 1]) ? value.slice(0, -1) : value;
  if (points.length < 3 || points.length > 100 || new Set(points.map(p => p.latitude + ':' + p.longitude)).size !== points.length ||
    Math.max(...points.map(p => p.longitude)) - Math.min(...points.map(p => p.longitude)) >= 180) return false;
  const turn = (a: Point, b: Point, c: Point) =>
    (b.longitude - a.longitude) * (c.latitude - a.latitude) - (b.latitude - a.latitude) * (c.longitude - a.longitude);
  const contains = (a: Point, b: Point, p: Point) => p.latitude >= Math.min(a.latitude, b.latitude) && p.latitude <= Math.max(a.latitude, b.latitude) &&
    p.longitude >= Math.min(a.longitude, b.longitude) && p.longitude <= Math.max(a.longitude, b.longitude);
  for (let i = 0; i < points.length; i++) for (let j = i + 1; j < points.length; j++) {
    if (j === i + 1 || (i === 0 && j === points.length - 1)) continue;
    const a = points[i], b = points[(i + 1) % points.length], c = points[j], d = points[(j + 1) % points.length];
    const abC = turn(a, b, c), abD = turn(a, b, d), cdA = turn(c, d, a), cdB = turn(c, d, b);
    if ((abC * abD < 0 && cdA * cdB < 0) || (!abC && contains(a, b, c)) || (!abD && contains(a, b, d)) ||
      (!cdA && contains(c, d, a)) || (!cdB && contains(c, d, b))) return false;
  }
  return Math.abs(points.reduce((area, point, i) => area + turn(points[0], point, points[(i + 1) % points.length]), 0)) > 0;
}
export function validViewport(view: MapViewport | undefined): view is MapViewport {
  return !!view && finite(view.latitude) && finite(view.longitude) && finite(view.zoom) &&
    view.latitude >= 29 && view.latitude <= 40 && view.longitude >= 32.5 && view.longitude <= 46 && view.zoom >= 5 && view.zoom <= 18;
}
export function normalizeHistory(entry: SearchHistoryEntry): SearchHistoryEntry {
  let criteria = structuredClone(entry.criteria);
  if (entry.mode === 'list') criteria = withoutViewport(criteria);
  else {
    const polygon = criteria['polygonFilter'];
    const polygonIsValid = validPolygon(polygon);
    if (!polygonIsValid) delete criteria['polygonFilter'];
    const [north, east, south, west] = boundsKeys.map(key => criteria[key]);
    if (polygonIsValid || ![north, east, south, west].every(finite) ||
      (north as number) <= (south as number) || (east as number) <= (west as number) ||
      Math.abs(north as number) > 90 || Math.abs(south as number) > 90 || Math.abs(east as number) > 180 || Math.abs(west as number) > 180) {
      for (const key of boundsKeys) delete criteria[key];
    }
  }
  return { ...entry, criteria, viewport: entry.mode === 'map' && validViewport(entry.viewport) ? entry.viewport : undefined };
}
function stable(value: unknown): string {
  const comparable = value && typeof value === 'object' && !Array.isArray(value)
    ? Object.fromEntries(Object.entries(value).filter(([key, item]) => !['limit', 'offset', 'sort', 'direction'].includes(key) &&
      item !== null && item !== undefined && item !== '' && !(Array.isArray(item) && item.length === 0) && !(key === 'onlyWithPrice' && item === false))) : value;
  return JSON.stringify(comparable, (_key, item) => item && typeof item === 'object' && !Array.isArray(item)
    ? Object.fromEntries(Object.keys(item).sort().map(key => [key, item[key]])) : item);
}
/** Transfer shared drafts, keeping geographic state exclusively in Map mode. */
export function sharedModeFilters<Q extends object>(state: { query?: Q; mapQuery?: Q; listDraft?: Q; mapDraft?: Q }, fromMap: boolean, toMap: boolean) {
  const source = fromMap ? state.mapDraft ?? state.mapQuery ?? state.query : state.listDraft ?? state.query;
  const shared = source ? withoutViewport(source) : undefined;
  const destination = toMap ? state.mapQuery : state.query;
  const changed = stable(shared ?? {}) !== stable(destination ? withoutViewport(destination) : {});
  return { changed, filters: {
    listDraft: shared && structuredClone(shared), mapDraft: shared && structuredClone(shared),
    query: !toMap ? shared && structuredClone(shared) : state.query,
    mapQuery: state.mapQuery,
  } };
}
